import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { inject } from '@angular/core';
import { filter } from 'rxjs/operators';
import { CanMatchFn, Route, Router, UrlSegment } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';

import { selectUser } from '@shared/store/selectors/user.selectors';
import { MaintenanceService } from '@data-access/system-controls/maintenance';

/**
 * Guard to check if the system is in maintenance mode and redirect users
 * to the maintenance page if they are not allowed access during maintenance.
 *
 * This guard should be applied to all routes except /maintenance and /auth/*
 */
const AUTH_WHITELIST = new Set([
  'auth/login',
  'auth/logout',
  'auth/login/callback',
]);

const getCandidatePaths = (route: Route, segments: UrlSegment[]): string[] => {
  const routePath = (route.path || '').replace(/^\//, '');
  const segmentPath = segments.map((segment) => segment.path).join('/');

  const paths = [routePath, segmentPath];
  if (routePath && segmentPath) {
    paths.push(`${routePath}/${segmentPath}`);
  }

  return paths.filter(Boolean);
};

export const MaintenanceGuard: CanMatchFn = async (route, segments) => {
  const router: Router = inject(Router);
  const maintenanceService: MaintenanceService = inject(MaintenanceService);
  const store: Store = inject(Store);
  const oktaAuth = inject(OKTA_AUTH);

  if (
    getCandidatePaths(route, segments).some((path) => AUTH_WHITELIST.has(path))
  ) {
    return true;
  }

  // Skip maintenance check if user is not authenticated (no token)
  if (!oktaAuth.getAccessToken()) {
    return true;
  }

  try {
    // Check maintenance status
    const status = await firstValueFrom(maintenanceService.getStatus());

    if (!status.active) {
      // No maintenance, allow access
      return true;
    }

    // Maintenance is active - wait for user to be available before checking bypass
    const user = await firstValueFrom(
      store.select(selectUser).pipe(filter((u) => u != null)),
    );

    if (maintenanceService.canBypassMaintenance(user)) {
      return true;
    }

    // Other users are redirected to maintenance page
    return router.createUrlTree(['/maintenance']);
  } catch (error) {
    console.error(
      '[MaintenanceGuard] Error checking maintenance status:',
      error,
    );
    // Fail open - allow access if maintenance check fails
    return true;
  }
};
